import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Package, Home, Images, Lock, Search, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getCatalog, getFabrics, fileUrl, logView } from "@/lib/api";
import { ImageZoom } from "@/components/ImageZoom";
import { Disclaimer } from "@/components/Disclaimer";
import { CustomerNav } from "@/components/CustomerNav";

export default function Catalogs() {
  const [items, setItems] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFabric = searchParams.get("fabric") || "";
  const [zoomSrc, setZoomSrc] = useState(null);

  useEffect(() => {
    getCatalog().then(setItems).catch(() => {}).finally(() => setLoading(false));
    getFabrics().then((fs) => setFabrics(fs || [])).catch(() => {});
    try {
      const c = JSON.parse(localStorage.getItem("cincstock_customer") || "null");
      if (c) logView({ ...c, type: "catalog", ref: activeFabric || "Catalogs" });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chips: fabrics that have at least one catalog image, plus the active one if any.
  const fabricChips = useMemo(() => {
    const present = new Set(items.map((it) => it.fabric).filter(Boolean));
    if (activeFabric) present.add(activeFabric);
    const ordered = fabrics.filter((f) => present.has(f.name)).map((f) => f.name);
    // include any tagged fabric not in the fabrics list (edge case)
    present.forEach((n) => { if (!ordered.includes(n)) ordered.push(n); });
    return ordered;
  }, [items, fabrics, activeFabric]);

  const setFabric = (name) => {
    const next = new URLSearchParams(searchParams);
    if (name) next.set("fabric", name);
    else next.delete("fabric");
    setSearchParams(next);
  };

  const filtered = useMemo(() => {
    let list = items;
    if (activeFabric) list = list.filter((it) => (it.fabric || "") === activeFabric);
    const q = query.trim().toLowerCase();
    if (q) {
      const tokens = q.split(/\s+/).filter(Boolean);
      list = list.filter((it) => {
        const hay = `${it.title || ""} ${it.fabric || ""} ${it.color || ""}`.toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    return list;
  }, [items, query, activeFabric]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <CustomerNav showBackHome />

      <Disclaimer />

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-10">
        <p className="text-[11px] uppercase tracking-[0.3em] text-white/40 mb-3">Look Book</p>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white">
            {activeFabric || "Catalogs"}
          </h1>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
            <Input
              data-testid="catalog-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search fabric type or colour…"
              className="rounded-none h-10 bg-[#141414] border-white/10 pl-9 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-white/30"
            />
          </div>
        </div>

        {/* Fabric filter chips */}
        {fabricChips.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="catalog-fabric-filter">
            <button
              onClick={() => setFabric("")}
              data-testid="catalog-chip-all"
              className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                !activeFabric ? "bg-white text-black border-white" : "bg-transparent text-white/70 border-white/20 hover:bg-white/10"
              }`}
            >
              All
            </button>
            {fabricChips.map((name) => (
              <button
                key={name}
                onClick={() => setFabric(name)}
                data-testid={`catalog-chip-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                  activeFabric === name ? "bg-white text-black border-white" : "bg-transparent text-white/70 border-white/20 hover:bg-white/10"
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        {loading && <p className="text-white/40 text-sm">Loading…</p>}
        {!loading && filtered.length === 0 && (
          <div className="border border-white/10 py-20 text-center text-white/40 text-sm" data-testid="catalog-empty">
            {activeFabric
              ? `No catalog pictures for ${activeFabric} yet.`
              : items.length === 0
                ? "No catalog images yet. The admin will add them soon."
                : "No catalogs match your search."}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((it) => (
            <figure key={it.id} className="group border border-white/10 bg-[#141414] overflow-hidden" data-testid={`catalog-item-${it.id}`}>
              <button
                onClick={() => setZoomSrc(fileUrl(it.storage_path))}
                data-testid={`catalog-zoom-${it.id}`}
                className="block w-full aspect-[4/5] overflow-hidden cursor-zoom-in"
              >
                <img
                  src={fileUrl(it.storage_path)}
                  alt={it.title || "Catalog"}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </button>
              {(it.title || it.fabric) && (
                <figcaption className="px-3 py-2 text-xs border-t border-white/10">
                  {it.title && <span className="block text-white/70 truncate">{it.title}</span>}
                  {it.fabric && <span className="block text-[10px] uppercase tracking-wider text-white/35">{it.fabric}</span>}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>

      <ImageZoom src={zoomSrc} alt="Catalog" open={!!zoomSrc} onOpenChange={(o) => !o && setZoomSrc(null)} />
    </div>
  );
}
